/* eslint-disable react/prop-types */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Spin,
  Card,
  DatePicker,
  Tabs,
  message,
  Typography,
} from 'antd';
import {
  DeleteOutlined,
  EnvironmentOutlined,
  InfoCircleOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import Direccion from 'components/Direccion';
import Table from 'components/Table';
import {
  RFC_PATTERN,
  INE_PATTERN,
} from 'utils/patterns';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import LadaNumero from 'components/LadaNumero';
import Select from 'components/Select';
import { onError } from 'utils/handlers';
import makeMessager from 'utils/complexFormsMessages';
import BusquedaAvanzada from 'views/Recaudacion/Caja/BusquedaAvanzadaPadrones';
import { makeGetOnFinish, makeOnChangeTabKey } from 'utils/tabsManagement';
import {
  getCiudadano,
  getCiudadanos,
} from 'api/cuentaunicasir';
import { DATE_FRONTEND_FORMAT, discartNullValues } from 'utils/formatValues';
import { getModelPermission } from 'utils/permission/index';
import { InputSearchPro } from 'components/InputSearch';
import { getTiposDePersonas } from 'api/catalogos/tipoDePersona';
import { getContenTypePorTipoDePadron, getEstadosDelPadron, getRegimenCapital } from 'api/catalogos';
import { getRegimenFiscales } from 'api/empresas/index';
import {
  baseURI,
  deleteContribuyente,
  getContribuyente,
  getContribuyentes,
  patchContribuyente,
  postContribuyente,
} from 'api/empresas/contribuyente';
import ActividadesEconomicas, {
  permissionActividadesEconomicasContribuyentes,
  permissionActividadesContribuyentes,
} from 'components/Catalogos/ActividadesEconomicas';
import ObligacionesFiscalesContribuyente, { permissionConfig as permissionConfigObligaciones } from 'components/Catalogos/ObligacionesFiscalesContribuyente';
import ContribuyentesVinculados, { permissionContribuyentesVinculados } from 'components/Catalogos/ContribuyentesVinculados';
import DeclaracionesEmpresarial, { permissionConfig as permissionConfigDeclaraciones } from 'components/Catalogos/DeclaracionesEmpresarial';
import EmpresasDeContribuyentes, { permissionConfig as permissionConfigEmpresas } from 'components/Catalogos/EmpresasDeContribuyentes';
import { capitalize, toUpper } from 'utils/normalizers';
import { getUnidadesDeRecaudacion } from 'api/configuracion/unidadesDeRecaudacion';
import useQuery from 'hooks/useQuery';
import { getNombreCompleto } from 'utils/padronesToString';
import { generateCURPRules } from 'utils/rules';
import useActualizadorDeCartera, { permissionConfigActionsActualizadorCartera } from 'hooks/useActualizadorDeCartera';

const { TabPane } = Tabs;

export const permissionConfig = {
  permissionModel: 'contribuyente',
  catalogsModels: ['ciudadano', 'regimencapital', 'tipodepersona'],
  tabsModels: [
    permissionConfigObligaciones,
    permissionContribuyentesVinculados,
    permissionConfigDeclaraciones,
    permissionActividadesEconomicasContribuyentes,
    permissionActividadesContribuyentes,
    permissionConfigEmpresas,
  ],
  customActions: permissionConfigActionsActualizadorCartera.filter((e) => e.padron === 15),
};

const Contribuyente = ({ permission }) => {
  const addressPermission = useMemo(() => getModelPermission('direccion'), []);
  const [form] = Form.useForm();
  const [formInfo] = Form.useForm();
  const [formSearch] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [currentTabKey, setCurrentTabKey] = useState('info');
  const [formAddress, setFormAddress] = useState();
  const [formAddressFiscal, setFormAddressFiscal] = useState();
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState({ results: [] });
  const [currentPage, setCurrentPage] = useState(1);
  const [tipoDePersona, setTipoDePersona] = useState([]);
  const [regimenCapital, setRegimenCapital] = useState([]);
  const [visibleBusquedaAvanzada, setVisibleBusquedaAvanzada] = useState(false);
  const [ciudadanoFound, setCiudadanoFound] = useState([]);
  const [dataExterno, setDataExterno] = useState([]);
  const selectedPersona = Form.useWatch('tipo_de_persona', formInfo);

  const [estadosDelPadron, setEstadosDelPadron] = useState([]);
  const [regimenFiscal, setRegimenFiscal] = useState([]);
  const [triggerCopy, setTriggerCopy] = useState(false);
  const [regimenFiscalContribuyente, setRegimenFiscalContribuyente] = useState(false);
  const [direccionDeNotificacion, setDireccionDeNotificacion] = useState(false);
  const [direccionFiscal, setDireccionFiscal] = useState(false);
  const [unidadesRecaudadoras, setUnidadesRecaudadoras] = useState(false);
  const params = useQuery(['id']);
  const [searchParams, setSearchParams] = useState({});
  const [justView, setJustView] = useState(false);
  const [contribuyenteSelected, setContribuyenteSelected] = useState();

  const { confirmUpdateCartera, updateCarteraAvailable } = useActualizadorDeCartera({
    tipoDePadronId: 15,
    padron: contribuyenteSelected,
    setLoading,
    permission,
  });

  const basicKeys = ['info', 'general'];
  const complexForms = [
    { key: 'direccion', formInstance: formAddress, permission: addressPermission },
    { key: 'direccion_notificacion', formInstance: formAddressFiscal, permission: addressPermission }];

  const fetchData = async (page = 1, q = null) => {
    setLoading(true);
    setCurrentPage(page);
    const _data = await getContribuyentes({
      ...(searchParams || {}),
      ...(q || {}),
      page,
      excluir_contribuyentes_especiales: true,
    });
    setData(_data);
    setLoading(false);
  };

  const showMsg = makeMessager(setCurrentTabKey, fetchData);

  const resetAndValidate = () => {
    setCiudadanoFound([]);
    formInfo.resetFields(['q']);
    formInfo.validateFields(['q']);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      // eslint-disable-next-line max-len
      const [_tipoDePersonas, _regimenCapital, _estadosDelPadron, _regimenFiscales, _unidadesDeRecaudacion] = await Promise.all([
        getTiposDePersonas(),
        getRegimenCapital(),
        getEstadosDelPadron(),
        getRegimenFiscales(),
        getUnidadesDeRecaudacion(),
      ]);
      if (mounted) {
        setTipoDePersona(_tipoDePersonas);
        setRegimenCapital(_regimenCapital);
        setEstadosDelPadron(_estadosDelPadron);
        setRegimenFiscal(_regimenFiscales);
        setUnidadesRecaudadoras(_unidadesDeRecaudacion);
        setLoading(false);
      }
    };
    if (permission.hasAllViewPermission) {
      fetchAll();
      if (params[0] !== null) {
        setTimeout(() => {
          // eslint-disable-next-line no-use-before-define
          onClickEdit(params[0]);
          setSelectedRowKeys([params[0]]);
        });
      }
    }
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    setVisible(false);
    form.resetFields();
    formInfo.resetFields();
    setCiudadanoFound([]);
    setSelectedRowKeys([]);
    setCurrentTabKey('info');
    setDireccionDeNotificacion(false);
    setDireccionFiscal(false);
    setData({ results: [] });
    setLoading(false);
    setJustView(false);
    setContribuyenteSelected();
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      await formSearch.validateFields();
      const paramsSearch = formSearch.getFieldsValue();
      const keys = Object.keys(paramsSearch);
      const validProps = keys.filter((prop) => paramsSearch[prop]);
      if (validProps?.length) {
        // const _data = await getContribuyentes(paramsSearch);
        // setData(_data);
        setSearchParams(paramsSearch);
        setCurrentPage(1);
        await fetchData(1, paramsSearch);
      } else {
        message.warn('Debe ingresar al menos un campo.');
      }

      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };
  const fetchCiudadano = async (id) => {
    setLoading(true);
    if (id) {
      const _ciudadano = await getCiudadano(id?.id || id);
      if (_ciudadano) {
        formInfo.setFieldsValue({
          ciudadano: _ciudadano?.id,
        });
        setCiudadanoFound([_ciudadano]);
      } else {
        message.info('No se encontró ciudadano');
        setCiudadanoFound([]);
      }
    }
    setLoading(false);
  };

  const searchCiudadano = async () => {
    try {
      setLoading(true);
      await formInfo.validateFields(['q']);
      const { q } = formInfo.getFieldsValue(['q']);
      if (q) {
        const _ciudadanos = await getCiudadanos({ q });
        if (_ciudadanos.results.length) {
          const [selected] = _ciudadanos.results;
          formInfo.setFieldsValue({
            ciudadano: selected.id,
          });
          setCiudadanoFound([selected]);
        } else if (!_ciudadanos.results.length) {
          message.info('No se encontró ciudadano');
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const tipoDePersonaOnChange = async (value) => {
    if (value === 1) {
      const regimen_fiscal_fisica = regimenFiscal.filter((e) => e.persona_fisica === true);
      setRegimenFiscalContribuyente(regimen_fiscal_fisica);
    } else if (value === 2) {
      const regimen_fiscal_moral = regimenFiscal.filter((e) => e.persona_moral === true);
      setRegimenFiscalContribuyente(regimen_fiscal_moral);
    }
  };

  const onClickEdit = async (id) => {
    const key = selectedRowKeys[0] || id;
    const match = await getContribuyente(key);
    const _direccion = match?.direcciones;
    const _direccion_de_notificacion = match?.direcciones_de_notificacion;
    if (_direccion) setDireccionFiscal(_direccion);
    if (_direccion_de_notificacion) setDireccionDeNotificacion(_direccion_de_notificacion);

    if (match) {
      if (match?.ciudadano) {
        await fetchCiudadano(match.ciudadano);
      }
      const formatted = match;
      formInfo.setFieldsValue({
        ...formatted,
        fecha_nacimiento: formatted?.fecha_nacimiento,
      });
      const _tipo_persona = formatted?.tipo_de_persona;
      if (_tipo_persona) tipoDePersonaOnChange(_tipo_persona);
      form.setFieldsValue(formatted);
      formInfo.setFieldsValue(formatted);
    }
    setVisible(true);
  };

  const concatNames = (v) => `${v.nombre} ${v.apellido_paterno || ''} ${v.apellido_materno || ''}`;

  const onFinish = async (_continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      await formInfo.validateFields();
      const values = {
        ...form.getFieldsValue(),
        ...formInfo.getFieldsValue(),
      };

      if (selectedPersona === 2) {
        values.nombre_completo = values.razon_social;
      } else {
        values.nombre_completo = concatNames(values);
      }

      if (values.ciudadano && ciudadanoFound.length) {
        const [ciudadano] = ciudadanoFound;
        values.ciudadano = ciudadano.id;
      }
      const [key] = selectedRowKeys;
      const _data = await (key ? patchContribuyente(key, values, false)
        : postContribuyente(values));
      if (_data) {
        if (_continue) {
          if (!key) {
            setSelectedRowKeys([_data.id]);
          }
          await onClickEdit(_data.id);
        }
        if (updateCarteraAvailable) {
          confirmUpdateCartera(_continue ? null : onCancel);
        } else if (!_continue) {
          onCancel();
        }
      }

      setLoading(false);
      return true;
    } catch (err) {
      onError(err, setLoading, [form, formInfo], setCurrentTabKey);
      return false;
    }
  };

  const deleteItem = async () => {
    setLoading(true);
    if (selectedRowKeys.length) {
      const [key] = selectedRowKeys;
      const deleted = await deleteContribuyente(key);
      if (deleted) {
        setVisibleAlert(false);
        onCancel();
      }
    }
    setLoading(false);
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
    formInfo.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    const regimen_fiscal_fisica = regimenFiscal.filter((e) => e?.persona_fisica === true);
    setRegimenFiscalContribuyente(regimen_fiscal_fisica);
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const cambiarAEdicion = (match) => {
    handleOnRowClick(match);
    onClickEdit(match.id);
  };

  const columns = [
    {
      titleText: 'Clave/Cuenta',
      dataIndex: 'clave',
      key: 'clave',
      width: 150,
    },
    {
      titleText: 'Tipo de Persona',
      dataIndex: 'tipo_de_persona',
      key: 'tipo_de_persona',
      width: 150,
      catalog: {
        cat: tipoDePersona,
      },
    },
    {
      titleText: 'RFC',
      dataIndex: 'rfc',
      key: 'rfc',
      width: 200,
    },
    {
      titleText: 'CURP',
      dataIndex: 'curp',
      key: 'curp',
      width: 200,
    },
    {
      titleText: 'Razón Social',
      dataIndex: 'razon_social',
      key: 'razon_social',
      width: 300,
    },
    {
      titleText: 'Teléfono',
      dataIndex: 'telefono_fijo',
      key: 'telefono_fijo',
      width: 200,
    },
    {
      titleText: 'Correo',
      dataIndex: 'correo_electronico',
      key: 'correo_electronico',
      width: 250,
    },
    {
      titleText: 'Estado',
      dataIndex: 'estado_del_padron',
      key: 'estado_del_padron',
      width: 200,
      catalog: {
        cat: estadosDelPadron,
      },
    },
  ];

  const callbackCURP = (match) => {
    if (match && !selectedRowKeys.length) {
      // SE ENCONTRÓ UNA COINCIDENCIA CON LA CURP INGRESADA CAMBIAR A EDICIÓN
      cambiarAEdicion(match);
    }
  };

  const rules = {

    required: [{ required: true }],
    telefono_fijo: [{ required: false }],
    RFC: [
      // else if (rfc !== null) {
      // throw new Error('Este campo es requerido.');
    // }
      {
        validator: async (_, rfc) => {
          if (rfc) {
            const _contribuyentes = await getContribuyentes({ rfc });
            const match = _contribuyentes?.results.find((e) => e.rfc === rfc);
            if (rfc !== null && !RFC_PATTERN.test(rfc)) {
              throw new Error('Este campo es requerido.');
            } else if (_contribuyentes.count
              && _contribuyentes?.results?.length
              && match
              && match?.id !== selectedRowKeys[0]) {
              throw new Error(`Ya existe un contribuyente con este RFC. - ${match?.clave}`);
            } else if (!RFC_PATTERN.test(rfc)) {
              throw new Error('Ingrese un RFC válido.');
            }
          }
        },
      },
    ],
    INE: [
      {
        pattern: INE_PATTERN,
        message: 'Ingrese un INE válido',
      },
    ],
    curp: generateCURPRules({
      validateUnique: true,
      tipoDePadron: 15,
      required: false,
      callback: callbackCURP,
    }),
    email: [
      { type: 'email' },
      // {
      //   validator: async (_, value) => {
      //     if (value) {
      //       const _contribuyentes = await getContribuyentes({ correo_electronico: value });
      //       const match = _contribuyentes?.results.find((e) => e.correo_electronico === value);
      //       if (_contribuyentes.count
      //         && _contribuyentes?.results?.length
      //         && match && (selectedRowKeys.length && match.id !== selectedRowKeys[0])) {
      //         throw new Error('Ya existe un contribuyente con este correo.');
      //       }
      //     } else {
      //       throw new Error('Este campo es requerido.');
      //     }
      //   },
      // },
      // { required: true },
    ],
    email_alternativo: [
      { type: 'email' },
      {
        validator: async (_, value) => {
          if (value !== null) {
            throw new Error('Ingresa correctamente el correo');
          }
        },
      },
    ],
  };

  const onChangeTabKey = makeOnChangeTabKey(
    basicKeys, complexForms, currentTabKey, setCurrentTabKey, showMsg,
  );

  const getOnFinish = makeGetOnFinish(basicKeys, complexForms, currentTabKey, onFinish);

  const phoneRules = {
    telefono_celular: [
      {
        validator: async (_, value) => {
          if (value && !(/^[0-9]{10}$/.test(value))) {
            throw new Error('Ingresa un número de teléfono valido');
          }
        },

      },
    ],
    telefono_fijo: [
      {
        validator: async (_, value) => {
          if (value && /^[0-9]{10}$/.test(value)) {
            throw new Error('Ingresa un número de teléfono valido');
          }
        },
      },
    ],
  };

  const setSameFields = async (isSame) => {
    setLoading(true);
    if (isSame) {
      const [key] = selectedRowKeys;
      const _contribuyente = await getContribuyente(key);
      if (_contribuyente?.direcciones) {
        const { direcciones } = _contribuyente;
        const {
          agencia,
          calle_principal,
          colonia_texto,
          cp_texto,
          descripcion_de_la_ubicacion,
          entre_calle,
          estado_texto,
          latitud,
          longitud,
          municipio_texto,
          numero_exterior,
          numero_interior,
          tipo_de_vialidad_entre,
          tipo_de_vialidad_y_calle,
          tipo_de_vialidad,
          y_calle,
        } = direcciones;
        const codigo_postal = direcciones.codigo_postal.d_codigo;
        const colonia = direcciones.codigo_postal.id;
        const estado = direcciones.codigo_postal.municipio.estado.nombre_de_AGEE;
        const municipio = direcciones.codigo_postal.municipio.nom_mun;
        const formValues = {
          agencia,
          calle_principal,
          codigo_postal,
          colonia_texto,
          colonia,
          cp_texto,
          descripcion_de_la_ubicacion,
          entre_calle,
          estado_texto,
          estado,
          latitud,
          longitud,
          municipio_texto,
          municipio,
          numero_exterior,
          numero_interior,
          tipo_de_vialidad_entre,
          tipo_de_vialidad_y_calle,
          tipo_de_vialidad,
          y_calle,
        };
        formAddressFiscal.setFieldsValue(discartNullValues(formValues));
      }
    } else {
      formAddressFiscal.resetFields();
    }
    setTriggerCopy(isSame);
    setLoading(false);
  };
  const nombre = getNombreCompleto(formInfo.getFieldsValue());
  const estado = estadosDelPadron.find((item) => item.id === form.getFieldValue('estado_del_padron'))?.descripcion;
  const rfc = formInfo.getFieldValue('rfc');

  const wipe = () => {
    formSearch.resetFields();
    setSearchParams({});
    setCurrentPage(1);
    setData({ results: [] });
  };
  return (
    <Row align="center" justify="center" className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {!visible ? (
          <>
            <Form
              layout="vertical"
              form={formSearch}
              scrollToFirstError
              onFinish={() => handleSearch()}
            >
              <Row gutter={10}>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="clave"
                    label="Clave"
                    hasFeedback
                    normalize={capitalize}
                  >
                    <Input allowClear />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="nombre"
                    label="Nombre"
                    hasFeedback
                  >
                    <Input allowClear />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="apellido_paterno"
                    label="Apellido paterno"
                    hasFeedback
                  >
                    <Input allowClear />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="apellido_materno"
                    label="Apellido materno"
                    hasFeedback
                  >
                    <Input allowClear />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="razon_social"
                    label="Razón Social"
                    hasFeedback
                  >
                    <Input allowClear />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="correo_electronico"
                    label="Correo electrónico"
                    hasFeedback
                  >
                    <Input allowClear />
                  </Form.Item>
                </Col>
                <Col md={16} sm={24} xs={12}>
                  <LadaNumero
                    form={form}
                    rules={rules}
                    fullSize
                    names={{
                      lada: 'lada',
                      numero_de_telefono: 'telefono_celular',
                    }}
                    celular
                  />
                </Col>
                <Col xs={24} sm={24} md={12}>
                  <Form.Item
                    name="rfc"
                    label="RFC"
                    normalize={capitalize}
                  >
                    <Input allowClear />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={12}>
                  <Form.Item
                    label="CURP"
                    name="curp"
                    normalize={capitalize}
                  >
                    <Input allowClear />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={10}>
                <Col xs={24} sm={24} md={6}>
                  <Button
                    htmlType="submit"
                    block
                    style={{ marginTop: 21 }}
                    icon={<SearchOutlined />}
                  >
                    Buscar
                  </Button>
                </Col>
                <Col xs={24} sm={24} md={6}>
                  <Button
                    onClick={wipe}
                    block
                    style={{ marginTop: 21 }}
                    icon={<DeleteOutlined />}
                  >
                    Limpiar Busqueda
                  </Button>
                </Col>
              </Row>
            </Form>
            <br />
            <Table
              cols={columns}
              mobileColumns={[columns[0], columns[1]]}
              data={data.results || []}
              rowSelection={rowSelection}
              handleOnRowClick={handleOnRowClick}
              controls={{
                onClickAdd,
                onClickEdit,
                onClickDelete,
                onClickExpand: () => {
                  setJustView(true);
                  onClickEdit();
                },
              }}
              allowExpand
              rowKey="id"
              baseURI={baseURI}
              permission={permission}
              pagination={false}
              paginationCallback={fetchData}
              count={data.count}
              currentPage={currentPage}
            />
          </>
        ) : (
          <Col span={24} style={{ height: '100%' }}>
            <Card
              className="form-container"
              title={`${selectedRowKeys.length ? (`Editando:  ${rfc} - ${nombre} ${estado ? ` (${estado})` : ''}`) : 'Agregar Contribuyente'} `}
              extra={(
                <FormSubmitControls
                  onFinish={justView ? null : getOnFinish()}
                  onCancel={onCancel}
                  allowSaveAndContinue={!justView}
                  onCancelText="Cancelar"
                  permission={permission}
                />
              )}
              bordered={false}
            >
              <Tabs
                onChange={onChangeTabKey}
                activeKey={currentTabKey}
                type="card"
              >
                <TabPane
                  tab={(
                    <>
                      <InfoCircleOutlined />
                      Datos de Identificación del Contribuyente
                    </>
                  )}
                  key="info"
                  forceRender
                >
                  <Form
                    name="info"
                    layout="vertical"
                    form={formInfo}
                    onFinish={() => onFinish()}
                    scrollToFirstError
                    initialValues={{ tipo_de_persona: 1 }}
                  >
                    <Row gutter={10} style={{ width: '100%' }}>
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="tipo_de_persona"
                          label="Tipo de Persona"
                          hasFeedback
                          rules={rules.required}
                        >
                          <Select
                            dataSource={tipoDePersona}
                            onChange={(value) => tipoDePersonaOnChange(value)}
                            allowClear
                            disabled={justView}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          normalize={toUpper}
                          name="rfc"
                          rules={rules.RFC}
                          label="RFC"
                          hasFeedback
                          validateTrigger="onBlur"
                          // required
                        >
                          <Input
                            disabled={justView}
                            maxLength={selectedPersona === 1 ? 13 : 12}
                            allowClear
                          />
                        </Form.Item>
                      </Col>
                      {selectedPersona === 2 ? (
                        <>
                          <Col xs={24} sm={24} md={8}>
                            <Form.Item
                              name="razon_social"
                              rules={rules.required}
                              label="Denominación o Razón Social"
                              hasFeedback
                              normalize={toUpper}
                            >
                              <Input disabled={justView} allowClear />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={24} md={8}>
                            <Form.Item
                              name="regimen_capital"
                              label="Régimen Capital"
                              hasFeedback
                            >
                              <Select disabled={justView} dataSource={regimenCapital} allowClear />
                            </Form.Item>
                          </Col>
                        </>
                      ) : (
                        <>
                          <Col xs={24} sm={24} md={8}>
                            <Form.Item
                              name="curp"
                              label="CURP"
                              rules={rules.curp}
                              hasFeedback
                              validateTrigger="onBlur"
                              normalize={toUpper}
                            >
                              <Input disabled={justView} allowClear />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={24} md={8}>
                            <Form.Item
                              name="nombre"
                              rules={rules.required}
                              label="Nombre"
                              hasFeedback
                              normalize={toUpper}
                            >
                              <Input disabled={justView} allowClear />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={24} md={8}>
                            <Form.Item
                              name="apellido_paterno"
                              label="Apellido paterno"
                              hasFeedback
                              normalize={toUpper}
                              rules={rules.required}
                            >
                              <Input disabled={justView} allowClear />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={24} md={8}>
                            <Form.Item
                              name="apellido_materno"
                              label="Apellido materno"
                              hasFeedback
                              normalize={toUpper}

                            >
                              <Input disabled={justView} allowClear />
                            </Form.Item>
                          </Col>
                        </>
                      )}

                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="nombre_comercial"
                          label="Nombre Comercial"
                          hasFeedback
                          normalize={toUpper}
                        >
                          <Input disabled={justView} allowClear />
                        </Form.Item>
                      </Col>
                      {
                        regimenFiscalContribuyente.length > 0 && (
                          <Col xs={24} sm={24} md={8}>
                            <Form.Item
                              name="regimen_fiscal"
                              label="Régimen Fiscal (SAT)"
                              hasFeedback
                            >
                              <Select
                                disabled={justView}
                                dataSource={regimenFiscalContribuyente}
                                allowClear
                              />
                            </Form.Item>
                          </Col>
                        )
                      }
                      {selectedPersona === 1 && (
                        <Col xs={24} sm={24} md={8} />
                      )}
                      <InputSearchPro
                        label="Ciudadano"
                        name="q"
                        disabled={justView}
                        onClickAdvanced={() => setVisibleBusquedaAvanzada(true)}
                        breakPoints={{
                          xs: 24,
                          sm: 24,
                          md: 8,
                        }}
                        inputProps={{
                          normalize: (e) => toUpper(e),
                          onPressEnter: () => searchCiudadano(),
                          onChange: ({ target: { value } }) => {
                            if (!value) {
                              resetAndValidate();
                            }
                          },
                        }}
                        resultProps={{
                          name: 'ciudadano',
                          label: 'Ciudadano',
                          dataSource: ciudadanoFound,
                          labelProp: 'nombre_completo',
                          onClear: resetAndValidate,
                          breakPoints: {
                            xs: 24,
                            sm: 24,
                            md: 16,
                          },
                        }}
                      />
                      <Col span={24} />
                      <Typography.Title level={3}>Datos de Contacto</Typography.Title>
                      <Col span={24} />
                      <Col md={8} sm={24} xs={24}>
                        <LadaNumero
                          disabled={justView}
                          form={form}
                          validateTrigger="onBlur"
                          rules={phoneRules}
                          fullSize
                          names={{
                            lada: 'lada_fijo',
                            numero_de_telefono: 'telefono_fijo',
                          }}
                          label="Teléfono Fijo"
                          required={false}
                        />
                      </Col>
                      <Col md={8} sm={24} xs={24}>
                        <LadaNumero
                          disabled={justView}
                          form={form}
                          rules={phoneRules}
                          fullSize
                          names={{
                            lada: 'lada_celular',
                            numero_de_telefono: 'telefono_celular',
                          }}
                          celular
                          label="Teléfono Celular"
                          required={false}
                        />
                      </Col>
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="correo_electronico"
                          rules={rules.email}
                          label="Correo electrónico"
                          hasFeedback
                          validateTrigger="onBlur"
                        >
                          <Input disabled={justView} allowClear />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Form.Item name="id" hidden>
                      <Input />
                    </Form.Item>
                    <Form.Item hidden>
                      <Button htmlType="submit" />
                    </Form.Item>
                  </Form>
                </TabPane>
                <TabPane
                  tab={(
                    <span>
                      <InfoCircleOutlined />
                      Datos del Registro al Padrón
                    </span>
                  )}
                  key="general"
                  forceRender
                >
                  <Form
                    name="general"
                    layout="vertical"
                    form={form}
                    onFinish={() => onFinish()}
                    scrollToFirstError
                  >
                    <Row gutter={10}>
                      {!!selectedRowKeys.length && (
                        <>
                          <Col xs={24} sm={24} md={8}>
                            <Form.Item
                              name="clave"
                              label="Clave/Cuenta"
                              hasFeedback
                              allowClear
                            >
                              <Input disabled />
                            </Form.Item>
                          </Col>
                        </>
                      )}
                      {/* unidadad recaudadora */}
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="unidad_de_recaudacion"
                          label="Unidad recaudadora"
                          hasFeedback
                        >
                          <Select
                            disabled={justView}
                            dataSource={unidadesRecaudadoras}
                            allowClear
                            onChange={(value) => {
                              if (value) {
                                form.setFieldsValue({
                                  unidad_de_recaudacion: value,
                                });
                              }
                            }}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={!selectedRowKeys?.length ? 16 : 8} />
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="fecha_de_alta_de_padron_estatal"
                          label="Fecha de Alta Padrón Estatal"
                          hasFeedback
                        >
                          <DatePicker
                            disabled={justView}
                            placeholder=""
                            format={DATE_FRONTEND_FORMAT}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="fecha_inicio_operaciones_contribuyente"
                          label="Fecha de Inicio de Operaciones"
                          hasFeedback
                        >
                          <DatePicker
                            disabled={justView}
                            placeholder=""
                            format={DATE_FRONTEND_FORMAT}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="estado_del_padron"
                          label="Estado del Padrón"
                          hasFeedback
                          rules={rules.required}
                        >
                          <Select disabled={justView} dataSource={estadosDelPadron} />
                        </Form.Item>
                      </Col>
                      {!!selectedRowKeys?.length && (
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="fecha_de_ultimo_movimiento"
                            label="Fecha de Último Movimiento"
                            hasFeedback
                          >
                            <DatePicker
                              placeholder=""
                              format={DATE_FRONTEND_FORMAT}
                              disabled
                            />
                          </Form.Item>
                        </Col>
                      )}
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="fecha_de_constitucion"
                          label={selectedPersona === 1 ? 'Fecha de Nacimiento' : 'Fecha de Constitución'}
                          hasFeedback
                        >
                          <DatePicker
                            disabled={justView}
                            placeholder=""
                            format={DATE_FRONTEND_FORMAT}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="fecha_de_alta_shcp"
                          label="Fecha de Alta SHCP/SAT"
                          hasFeedback
                          tooltip="Fecha de alta ante la Secretaria de Hacienda y
                            crédito público y/o SAT"
                        >
                          <DatePicker
                            disabled={justView}
                            placeholder=""
                            format={DATE_FRONTEND_FORMAT}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Form.Item hidden>
                      <Button htmlType="submit" />
                    </Form.Item>
                  </Form>
                </TabPane>
                {addressPermission?.view && (
                  <TabPane
                    tab={(
                      <>
                        <EnvironmentOutlined />
                        Domicilio Fiscal
                      </>
                    )}
                    key="direccion"
                    forceRender={selectedRowKeys?.length}
                    disabled={!selectedRowKeys.length}
                  >
                    <Direccion
                      mutable={!justView}
                      currentURI={baseURI}
                      parentID={selectedRowKeys[0]}
                      setLoading={setLoading}
                      onError={onError}
                      setFormAddress={setFormAddress}
                      setCurrentTabKey={setCurrentTabKey}
                      allowVerify
                      addressPropName="direcciones"
                      callback={(values, _continue) => {
                        if (!_continue) {
                          onCancel();
                        } else {
                          const [key] = selectedRowKeys;
                          const clone = [...data?.results];
                          const mappedData = clone?.map((e) => (e.id === key
                            ? ({ ...e, direcciones: values?.id }) : e));
                          setData(mappedData);
                        }
                      }}
                      permission={addressPermission}
                    />
                  </TabPane>
                )}
                {addressPermission?.view && (
                  <TabPane
                    tab={(
                      <>
                        <EnvironmentOutlined />
                        Domicilio para recibir Notificaciones
                      </>
                    )}
                    key="direccion_notificacion"
                    forceRender={selectedRowKeys?.length}
                    disabled={!selectedRowKeys.length}
                  >
                    <Form layout="vertical">
                      <Row>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="misma_direccion_fiscal"
                            label="¿Misma Dirección Fiscal?"
                            hasFeedback
                          >
                            <Select disabled={justView} trueFalse onChange={setSameFields} />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Form>
                    <Direccion
                      mutable={!justView}
                      currentURI={baseURI}
                      parentID={selectedRowKeys[0]}
                      setLoading={setLoading}
                      onError={onError}
                      setFormAddress={setFormAddressFiscal}
                      setCurrentTabKey={setCurrentTabKey}
                      allowVerify
                      addressPropName="direcciones_de_notificacion"
                      callback={(values, _continue) => {
                        if (!_continue) {
                          onCancel();
                        } else {
                          const [key] = selectedRowKeys;
                          const clone = [...data?.results];
                          const mappedData = clone?.map((e) => (e.id === key
                            ? ({ ...e, direcciones_de_notificacion: values?.id }) : e));
                          setData(mappedData);
                        }
                      }}
                      permission={addressPermission}
                      triggerClone={triggerCopy}
                    />
                  </TabPane>
                )}
                {permission.tabs[permissionConfigEmpresas.model].hasAllViewPermission && (
                  <TabPane
                    key="empresas"
                    forceRender={selectedRowKeys.length}
                    disabled={!selectedRowKeys.length}
                    tab={(
                      <>
                        <EnvironmentOutlined />
                        Empresas
                      </>
                    )}
                  >
                    <EmpresasDeContribuyentes
                      mutable={!justView}
                      contribuyente={selectedRowKeys[0]}
                      permission={permission.tabs[permissionConfigEmpresas.model]}
                      direccionFiscal={direccionFiscal}
                      direccionNotificacion={direccionDeNotificacion}
                    />
                  </TabPane>
                )}
                {permission.tabs[permissionConfigObligaciones.model].hasAllViewPermission && (
                  <TabPane
                    key="obligaciones_fiscales"
                    forceRender={!!selectedRowKeys.length}
                    disabled={!selectedRowKeys.length}
                    tab={(
                      <>
                        <EnvironmentOutlined />
                        Obligaciones Fiscales
                      </>
                    )}
                  >
                    <ObligacionesFiscalesContribuyente
                      permission={permission.tabs[permissionConfigObligaciones.model]}
                      contribuyente={selectedRowKeys[0]}
                      contribuyenteRFC={rfc}
                      mutable={!justView}
                    />
                  </TabPane>
                )}
                {permission.tabs[permissionActividadesContribuyentes.model]
                  .hasAllViewPermission && (
                  <TabPane
                    key="actividades_economicas_contribuyente"
                    forceRender={selectedRowKeys.length}
                    disabled={!selectedRowKeys.length}
                    tab={(
                      <>
                        <EnvironmentOutlined />
                        Actividades Económicas
                      </>
                    )}
                  >
                    <ActividadesEconomicas
                      permission={permission.tabs[permissionActividadesContribuyentes.model]}
                      contribuyente={selectedRowKeys[0]}
                      mutable={!justView}
                    />
                  </TabPane>
                )}
                {permission.tabs[permissionActividadesEconomicasContribuyentes.model]
                  .hasAllViewPermission && (
                  <TabPane
                    key="actividades_economicas"
                    forceRender={selectedRowKeys.length}
                    disabled={!selectedRowKeys.length}
                    tab={(
                      <>
                        <EnvironmentOutlined />
                        Actividades Económicas (SAT)
                      </>
                    )}
                  >
                    <ActividadesEconomicas
                      permission={permission
                        .tabs[permissionActividadesEconomicasContribuyentes.model]}
                      contribuyente={selectedRowKeys[0]}
                      sat
                      mutable={!justView}
                    />
                  </TabPane>
                )}
                {permission.tabs[permissionContribuyentesVinculados.model].hasAllViewPermission && (
                  <TabPane
                    key="contribuyentes_vinculados"
                    forceRender={selectedRowKeys.length}
                    disabled={!selectedRowKeys.length}
                    tab={(
                      <>
                        <EnvironmentOutlined />
                        Contribuyentes Vinculados
                      </>
                    )}
                  >
                    <ContribuyentesVinculados
                      permission={permission.tabs[permissionContribuyentesVinculados.model]}
                      contribuyente={selectedRowKeys[0]}
                      mutable={!justView}
                    />
                  </TabPane>
                )}
                {permission.tabs[permissionConfigDeclaraciones.model].hasAllViewPermission && (
                  <TabPane
                    key="declaraciones"
                    forceRender={selectedRowKeys.length}
                    disabled={!selectedRowKeys.length || justView}
                    tab={(
                      <>
                        <EnvironmentOutlined />
                        Declaraciones
                      </>
                      )}
                  >
                    <DeclaracionesEmpresarial
                      contribuyente={selectedRowKeys[0]}
                      content_type_padron={getContenTypePorTipoDePadron(16)}
                    />
                  </TabPane>
                )}
              </Tabs>
            </Card>
          </Col>
        )}
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          visible={visibleAlert}
          content="Contribuyente Seleccionado"
          loading={loading}
        />
        <BusquedaAvanzada
          tipoDePadron={1}
          visible={visibleBusquedaAvanzada}
          setVisible={setVisibleBusquedaAvanzada}
          callback={fetchCiudadano}
          dataExterno={dataExterno}
          setDataExterno={setDataExterno}
        />
      </Spin>
    </Row>
  );
};

export default Contribuyente;
